"use client"

import { useRef, useState, useEffect } from "react"
import { Input } from "../ui/input"
import { Button } from "../ui/button"
import { toast } from "sonner"
import { post } from "../../lib/api"
import { readFileWithEncodingDetection } from "../../lib/encoding-utils"

// InfoOverlay component
const InfoOverlay = ({ open, onClose }) => {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 ">
            <div className="bg-[#ffffff] rounded-lg shadow-lg max-w-lg w-full p-6 relative dark:bg-[#2c313c] dark:text-white">
                <button
                    className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-2xl font-bold"
                    onClick={onClose}
                    aria-label="Close"
                >
                    ×
                </button>
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none"/>
                        <path d="M12 16v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M12 8h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    How does the Word Search uploader work?
                </h2>
                <div className="space-y-3 text-gray-800 text-sm dark:text-white">
                    <p>
                        Upload an <b>.xml file</b> containing a word search puzzle in Crossword Compiler format. The uploader will parse the XML and extract the grid and word list.
                    </p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Click the blue button to upload an .xml file with word search data.</li>
                        <li>The XML should contain grid cells and word definitions.</li>
                        <li>After loading, you can preview the puzzle and schedule the challenge.</li>
                        <li>If you upload multiple XML files, they will be scheduled for consecutive days.</li>
                    </ul>
                </div>
            </div>
        </div>
    );
}

const WordSearchPlanning = ({ game_id, onUpdate }) => {
    const [puzzles, setPuzzles] = useState([])
    const [currentPuzzleIndex, setCurrentPuzzleIndex] = useState(0)
    const [scheduledDate, setScheduledDate] = useState("")
    const [lastUploadedDate, setLastUploadedDate] = useState(null)
    const [isUploading, setIsUploading] = useState(false)
    const fileInputRef = useRef(null)
    const [showInfo, setShowInfo] = useState(false)

    // Initialize with today's date
    useEffect(() => {
        const today = new Date()
        const todayString = today.toISOString().split("T")[0]
        setScheduledDate(todayString)
    }, [])

    // Update default date after upload
    useEffect(() => {
        if (lastUploadedDate) {
            const nextDay = new Date(lastUploadedDate)
            nextDay.setDate(nextDay.getDate() + 1)
            const nextDayString = nextDay.toISOString().split("T")[0]
            setScheduledDate(nextDayString)
        }
    }, [lastUploadedDate])

    const parseXML = (xmlText) => {
        try {
            const parser = new DOMParser()
            const xmlDoc = parser.parseFromString(xmlText, "text/xml")
            
            // Check for parsing errors
            const parseError = xmlDoc.getElementsByTagName("parsererror")
            if (parseError.length > 0) {
                throw new Error("Invalid XML format")
            }

            // Extract metadata
            const metadata = xmlDoc.querySelector("metadata")
            const title = metadata?.querySelector("title")?.textContent || "Untitled"
            const creator = metadata?.querySelector("creator")?.textContent || "Unknown"
            const description = metadata?.querySelector("description")?.textContent || ""

            // Extract grid dimensions
            const grid = xmlDoc.querySelector("grid")
            const width = parseInt(grid?.getAttribute("width") || "0")
            const height = parseInt(grid?.getAttribute("height") || "0")

            // Extract grid cells
            const cells = []
            const cellElements = xmlDoc.querySelectorAll("cell")
            cellElements.forEach(cell => {
                const x = parseInt(cell.getAttribute("x"))
                const y = parseInt(cell.getAttribute("y"))
                const solution = cell.getAttribute("solution")
                cells.push({ x, y, solution })
            })

            // Extract words
            const words = []
            const wordElements = xmlDoc.querySelectorAll("word")
            wordElements.forEach(word => {
                const id = word.getAttribute("id")
                const solution = word.getAttribute("solution")
                const x = word.getAttribute("x")
                const y = word.getAttribute("y")
                
                // Handle both coordinate formats
                let coordinates = null
                if (x && y) {
                    coordinates = { x, y }
                } else {
                    // Handle cells format for complex words
                    const cellElements = word.querySelectorAll("cells")
                    if (cellElements.length > 0) {
                        coordinates = Array.from(cellElements).map(cell => ({
                            x: cell.getAttribute("x"),
                            y: cell.getAttribute("y")
                        }))
                    }
                }
                
                words.push({ id, solution, coordinates })
            })

            // Extract clues
            const clues = []
            const clueElements = xmlDoc.querySelectorAll("clue")
            clueElements.forEach(clue => {
                const wordId = clue.getAttribute("word")
                const clueText = clue.textContent
                clues.push({ wordId, clueText })
            })

            return {
                title,
                creator,
                description,
                width,
                height,
                cells,
                words,
                clues,
                xmlText // Keep original XML for upload
            }
        } catch (error) {
            console.error("XML parsing error:", error)
            throw new Error("Failed to parse XML file")
        }
    }

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0]
        if (!file || !file.name.endsWith('.xml')) {
            toast.error("Please upload a valid .xml file.")
            return
        }

        try {
            const text = await readFileWithEncodingDetection(file)
            const puzzle = parseXML(text)
            
            if (!puzzle.width || !puzzle.height || puzzle.cells.length === 0) {
                toast.error("Invalid word search puzzle: missing grid data.")
                return
            }

            if (puzzle.words.length === 0) {
                toast.error("Invalid word search puzzle: no words found.")
                return
            }

            setPuzzles([puzzle])
            setCurrentPuzzleIndex(0)
            toast.success(`Loaded word search puzzle: "${puzzle.title}" with ${puzzle.words.length} words!`)
        } catch (error) {
            toast.error(`Failed to parse XML: ${error.message}`)
            console.error("File reading error:", error)
        }
    }

    const getCurrentPuzzle = () => puzzles[currentPuzzleIndex] || null

    const nextPuzzle = () => {
        if (currentPuzzleIndex < puzzles.length - 1) {
            setCurrentPuzzleIndex(currentPuzzleIndex + 1)
        }
    }

    const prevPuzzle = () => {
        if (currentPuzzleIndex > 0) {
            setCurrentPuzzleIndex(currentPuzzleIndex - 1)
        }
    }

    const formatDateForDisplay = (dateString) => {
        const date = new Date(dateString)
        return date.toLocaleDateString("en-US", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
        })
    }

    const renderGrid = (puzzle) => {
        if (!puzzle) return null

        // Create 2D grid from cells
        const grid = Array(puzzle.height).fill(null).map(() => Array(puzzle.width).fill(''))
        
        puzzle.cells.forEach(cell => {
            if (cell.x > 0 && cell.x <= puzzle.width && cell.y > 0 && cell.y <= puzzle.height) {
                grid[cell.y - 1][cell.x - 1] = cell.solution
            }
        })

        return (
            <div className="flex justify-center py-4">
                <div className="inline-block border-2 border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                    {grid.map((row, rowIndex) => (
                        <div key={rowIndex} className="flex">
                            {row.map((cell, colIndex) => (
                                <div
                                    key={`${rowIndex}-${colIndex}`}
                                    className="w-8 h-8 border border-gray-200 dark:border-gray-700 flex items-center justify-center text-sm font-bold bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                >
                                    {cell || ' '}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        )
    }

    const handleUploadPuzzle = async () => {
        const puzzle = getCurrentPuzzle()

        if (!puzzle) {
            toast.error("No valid puzzle to upload.")
            return
        }

        if (!scheduledDate) {
            toast.error("Please select a date for the challenge.")
            return
        }

        setIsUploading(true)

        try {
            // Create challenge data object
            const challengeData = {
                title: puzzle.title,
                creator: puzzle.creator,
                description: puzzle.description,
                width: puzzle.width,
                height: puzzle.height,
                cells: puzzle.cells,
                words: puzzle.words,
                clues: puzzle.clues,
                xml: puzzle.xmlText
            }

            // First API call - Add the challenge
            const response = await post(
                {
                    method: "addChallengeNoDup",
                    game_id: game_id,
                    challenge_id: puzzle.title, // Use title as challenge ID
                    data: JSON.stringify(challengeData), // Store all puzzle data as JSON
                    variant_id: 1,
                    variant_name: "challenge",
                },
                "/challenges",
            )

            if (response.error) {
                if (response.error === "Failed to fetch") {
                    toast.error("A challenge with that name already exists.")
                } else {
                    toast.error(response.error)
                }
                return
            }

            // Second API call - Schedule the daily challenge
            const dailyResponse = await post(
                {
                    method: "addDailyChallenge",
                    game_id: game_id,
                    date: scheduledDate,
                    daily_challenge_info: puzzle.title, // Use title as daily challenge info
                },
                "/challenges",
            )

            if (dailyResponse.error) {
                toast.error(`Challenge uploaded but scheduling failed: ${dailyResponse.error}`)
                return
            }

            toast.success(`Word Search puzzle "${puzzle.title}" uploaded and scheduled for ${formatDateForDisplay(scheduledDate)}!`)
            setLastUploadedDate(scheduledDate)
            onUpdate?.()

            // If there are more puzzles, move to the next one
            if (currentPuzzleIndex < puzzles.length - 1) {
                setCurrentPuzzleIndex(currentPuzzleIndex + 1)
                // Update the scheduled date to the next day
                const nextDay = new Date(scheduledDate)
                nextDay.setDate(nextDay.getDate() + 1)
                const nextDayString = nextDay.toISOString().split("T")[0]
                setScheduledDate(nextDayString)
            }
        } catch (err) {
            toast.error("Upload failed!")
            console.error(err)
        } finally {
            setIsUploading(false)
        }
    }

    const handleUploadAllPuzzles = async () => {
        if (puzzles.length === 0) {
            toast.error("No puzzles to upload.")
            return
        }

        if (!scheduledDate) {
            toast.error("Please select a start date for the challenges.")
            return
        }

        setIsUploading(true)

        try {
            let currentDate = new Date(scheduledDate)
            let successCount = 0

            for (let i = 0; i < puzzles.length; i++) {
                const puzzle = puzzles[i]
                const dateString = currentDate.toISOString().split("T")[0]

                const challengeData = {
                    title: puzzle.title,
                    creator: puzzle.creator,
                    description: puzzle.description,
                    width: puzzle.width,
                    height: puzzle.height,
                    cells: puzzle.cells,
                    words: puzzle.words,
                    clues: puzzle.clues,
                    xml: puzzle.xmlText
                }

                // First API call - Add the challenge
                const response = await post(
                    {
                        method: "addChallengeNoDup",
                        game_id: game_id,
                        challenge_id: puzzle.title,
                        data: JSON.stringify(challengeData),
                        variant_id: 1,
                        variant_name: "challenge",
                    },
                    "/challenges",
                )

                if (response.error) {
                    if (response.error !== "Failed to fetch") {
                        toast.error(`Failed to upload "${puzzle.title}": ${response.error}`)
                        continue
                    }
                }

                // Second API call - Schedule the daily challenge
                const dailyResponse = await post(
                    {
                        method: "addDailyChallenge",
                        game_id: game_id,
                        date: dateString,
                        daily_challenge_info: puzzle.title,
                    },
                    "/challenges",
                )

                if (dailyResponse.error) {
                    toast.error(`Challenge "${puzzle.title}" uploaded but scheduling failed: ${dailyResponse.error}`)
                } else {
                    successCount++
                }

                // Move to next day
                currentDate.setDate(currentDate.getDate() + 1)
            }

            if (successCount > 0) {
                toast.success(`Successfully uploaded ${successCount} out of ${puzzles.length} Word Search puzzles!`)
                setLastUploadedDate(currentDate.toISOString().split("T")[0])
                onUpdate?.()
            }
        } catch (err) {
            toast.error("Upload failed!")
            console.error(err)
        } finally {
            setIsUploading(false)
        }
    }

    const currentPuzzle = getCurrentPuzzle()

    return (
        <>
            <div className="space-y-4">
                {/* File Upload */}
                <div className="flex items-center gap-3 mb-2">
                    <Input type="file" accept=".xml" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileChange} />
                    <Button
                        className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 text-white border border-blue-700 dark:border-blue-700 px-6 py-3 font-semibold shadow-md"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        Upload Word Search XML File
                    </Button>
                </div>
            </div>

            {puzzles.length > 0 && currentPuzzle && (
                <>
                    {/* Puzzle Navigation */}
                    {puzzles.length > 1 && (
                        <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 mb-2">
                            <Button
                                variant="outline"
                                className="border-gray-400 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white px-4 py-2 font-medium shadow-sm"
                                onClick={prevPuzzle}
                                disabled={currentPuzzleIndex === 0}
                            >
                                Previous Puzzle
                            </Button>
                            <div className="text-center">
                                <div className="font-bold text-lg text-gray-900 dark:text-white">
                                    Puzzle {currentPuzzleIndex + 1} of {puzzles.length}: {currentPuzzle.title}
                                </div>
                                <div className="text-sm text-gray-600 dark:text-gray-300">
                                    {currentPuzzle.width}×{currentPuzzle.height} grid with {currentPuzzle.words.length} words
                                </div>
                            </div>
                            <Button
                                variant="outline"
                                className="border-gray-400 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white px-4 py-2 font-medium shadow-sm"
                                onClick={nextPuzzle}
                                disabled={currentPuzzleIndex === puzzles.length - 1}
                            >
                                Next Puzzle
                            </Button>
                        </div>
                    )}

                    {/* Single puzzle display */}
                    {puzzles.length === 1 && (
                        <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 mb-2">
                            <div className="font-bold text-lg text-gray-900 dark:text-white">{currentPuzzle.title}</div>
                            <div className="text-sm text-gray-600 dark:text-gray-300">
                                {currentPuzzle.width}×{currentPuzzle.height} grid with {currentPuzzle.words.length} words
                            </div>
                        </div>
                    )}

                    {/* Grid Display */}
                    <div className="p-4 bg-[#ffffff] dark:bg-[#2c313c] border-2 border-gray-200 dark:border-gray-700 rounded-xl shadow-lg mb-2">
                        <div className="text-center mb-4">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Word Search Grid</h3>
                            <div className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                                {currentPuzzle.description && `Theme: ${currentPuzzle.description}`}
                            </div>
                        </div>
                        {renderGrid(currentPuzzle)}
                    </div>

                    {/* Puzzle Info */}
                    <div className="p-4 bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-900 rounded-lg mb-2">
                        <div className="text-base text-gray-900 dark:text-white space-y-1">
                            <div>
                                <strong>Title:</strong> {currentPuzzle.title}
                            </div>
                            <div>
                                <strong>Creator:</strong> {currentPuzzle.creator}
                            </div>
                            <div>
                                <strong>Grid Size:</strong> {currentPuzzle.width} × {currentPuzzle.height}
                            </div>
                            <div>
                                <strong>Words:</strong> {currentPuzzle.words.length}
                            </div>
                            {currentPuzzle.description && (
                                <div>
                                    <strong>Description:</strong> {currentPuzzle.description}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Words List */}
                    <div className="p-4 bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-900 rounded-lg mb-2">
                        <h3 className="font-bold text-gray-900 dark:text-white mb-2">Words to Find:</h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                            {currentPuzzle.words.map((word, index) => (
                                <div key={index} className="text-sm text-gray-700 dark:text-gray-300">
                                    {word.solution}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Date Scheduling */}
                    <div className="p-4 bg-purple-50 dark:bg-purple-900 border border-purple-200 dark:border-purple-900 rounded-lg mb-2">
                        <div className="space-y-3">
                            <label htmlFor="scheduledDate" className="block text-sm font-medium text-gray-900 dark:text-white">
                                Schedule Challenge Date:
                            </label>
                            <div className="flex items-center gap-4">
                                <Input
                                    type="date"
                                    id="scheduledDate"
                                    value={scheduledDate}
                                    onChange={(e) => setScheduledDate(e.target.value)}
                                    className="max-w-xs text-gray-900 dark:text-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600"
                                />
                                <div className="text-sm text-gray-600 dark:text-gray-300">
                                    {scheduledDate && `Will be scheduled for: ${formatDateForDisplay(scheduledDate)}`}
                                </div>
                            </div>
                            {lastUploadedDate && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                    Last upload was scheduled for {formatDateForDisplay(lastUploadedDate)}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Upload Buttons */}
                    <div className="space-y-2">
                        <Button
                            className="w-full bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 text-white border border-green-700 dark:border-green-700 py-3 font-semibold shadow-md"
                            onClick={handleUploadPuzzle}
                            disabled={isUploading}
                        >
                            {isUploading ? "Uploading..." : `Schedule This Puzzle for ${scheduledDate ? formatDateForDisplay(scheduledDate) : "Selected Date"}`}
                        </Button>
                        
                        {puzzles.length > 1 && (
                            <Button
                                className="w-full bg-purple-600 hover:bg-purple-700 dark:bg-purple-700 dark:hover:bg-purple-800 text-white border border-purple-700 dark:border-purple-700 py-3 font-semibold shadow-md"
                                onClick={handleUploadAllPuzzles}
                                disabled={isUploading}
                            >
                                {isUploading ? "Uploading All..." : `Upload All ${puzzles.length} Puzzles (Consecutive Days)`}
                            </Button>
                        )}
                    </div>
                </>
            )}

            {puzzles.length === 0 && (
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 p-3 rounded-md mb-4 mt-4">
                    <span>
                        📁 Upload an .xml file with word search puzzle data to get started
                    </span>
                    <button
                        className="ml-2 p-1 rounded-full flex items-center justify-center"
                        onClick={() => setShowInfo(true)}
                        aria-label="How does the uploader work?"
                        type="button"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none"/>
                            <path d="M12 16v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            <path d="M12 8h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                    </button>
                </div>
            )}
            <InfoOverlay open={showInfo} onClose={() => setShowInfo(false)} />
        </>
    )
}

export default WordSearchPlanning 